/**
 * PinQualityPanel — scans every generated Pinterest pin and flags
 * the ones failing the deterministic Pillow QA checks added in
 * v4.96 (`headline_present`, `lockup_contrast`, plus the existing
 * file_size / dimensions / aspect / solid-color / lockup_presence
 * gates).
 *
 * Lives on the Admin → Issues snapshot page so the team can see
 * "these 7 pins shipped broken" in ONE place, instead of scrolling
 * the content library hunting for them. Zero LLM cost — all checks
 * are local Pillow inspections.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ImageOff, RefreshCw, Loader2, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";


function FailRow({ asset }) {
    return (
        <div
            className="border border-bl rounded-md p-3 bg-bl-surface/40"
            data-testid={`pin-quality-row-${asset.asset_id}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="space-y-0.5 min-w-0">
                    <div className="text-xs font-mono-bl text-bl-muted">
                        {asset.asset_type} · pkg{" "}
                        <Link
                            to={`/admin/content?package_id=${asset.package_id}`}
                            className="underline underline-offset-2 hover:text-bl-text"
                        >
                            {asset.package_id}
                        </Link>
                    </div>
                    <div className="text-sm text-bl">
                        {asset.summary}
                    </div>
                    {asset.fail_reasons?.length > 0 && (
                        <div className="text-xs text-bl-muted">
                            failed: {asset.fail_reasons.join(", ")}
                        </div>
                    )}
                </div>
                <img
                    src={`${API}/content-images/${asset.asset_id}`}
                    alt={asset.asset_type}
                    className="w-16 h-24 object-cover rounded-sm border border-bl bg-bl-surface flex-shrink-0"
                    onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
                />
            </div>
        </div>
    );
}


export default function PinQualityPanel() {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    async function load(silent = false) {
        if (!silent) setLoading(true);
        try {
            const { data } = await axios.get(
                `${API}/admin/issues/pin-quality`,
                { params: { limit: 300, only_failing: true }, withCredentials: true },
            );
            setData(data);
        } catch (e) {
            if (!silent) toast.error("Failed to scan pin quality");
        } finally {
            setLoading(false);
        }
    }

    // Auto-load when first expanded — keeps the snapshot page light
    // on initial render (the scan touches every pin's PNG on disk).
    useEffect(() => {
        if (open && data === null) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const failing = data?.failing ?? null;
    const scanned = data?.scanned ?? null;
    const missingLocal = data?.missing_local_file ?? 0;

    return (
        <div
            className="bl-card border border-bl overflow-hidden"
            data-testid="pin-quality-panel"
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-bl-surface/40 text-left"
                data-testid="pin-quality-toggle"
            >
                <div className="flex items-center gap-2.5">
                    {failing && failing > 0 ? (
                        <AlertTriangle className="h-4 w-4 text-bl-amber" />
                    ) : (
                        <ImageOff className="h-4 w-4 text-bl-muted" />
                    )}
                    <div>
                        <div className="text-sm font-semibold text-bl">
                            Pin quality scan
                        </div>
                        <div className="text-xs text-bl-muted">
                            {data
                                ? failing > 0
                                    ? `${failing} pin${failing === 1 ? "" : "s"} failing checks (scanned ${scanned})`
                                    : `All ${scanned} pins pass · 0 issues`
                                : "Click to scan — headline + lockup contrast across the library"}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {open && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); load(); }}
                            disabled={loading}
                            className="bl-btn-ghost text-xs px-2 py-1 flex items-center gap-1 disabled:opacity-50"
                            data-testid="pin-quality-refresh"
                            aria-label="Re-run scan"
                        >
                            {loading ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <RefreshCw className="h-3 w-3" />
                            )}
                        </button>
                    )}
                    {open ? (
                        <ChevronDown className="h-4 w-4 text-bl-muted" />
                    ) : (
                        <ChevronRight className="h-4 w-4 text-bl-muted" />
                    )}
                </div>
            </button>

            {open && (
                <div className="border-t border-bl px-4 py-3 space-y-2">
                    {loading && !data && (
                        <div className="text-sm text-bl-muted flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Scanning every pin (this can take 10-30 s)…
                        </div>
                    )}
                    {data && failing === 0 && (
                        <div className="text-sm text-bl-green flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4" />
                            All {scanned} pins pass deterministic QA. Nothing to fix.
                        </div>
                    )}
                    {data && failing > 0 && (
                        <>
                            <div className="text-xs text-bl-muted">
                                Click any row to open its package. Re-render via the row&apos;s &quot;All&quot; button.
                            </div>
                            <div className="space-y-2">
                                {(data.results || []).map((a) => (
                                    <FailRow key={a.asset_id} asset={a} />
                                ))}
                            </div>
                        </>
                    )}
                    {missingLocal > 0 && (
                        <div className="text-xs text-bl-amber bg-bl-amber-tint/30 border border-bl-amber/30 rounded-md p-2">
                            {missingLocal} pin{missingLocal === 1 ? "" : "s"} couldn&apos;t be scanned — local PNG missing on this env (likely rendered on the other env). Set up R2 or re-render to fix.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
